import type { ErrorEvent, EventHint } from '@sentry/nextjs';

/**
 * Filter out browser extension and third-party script errors
 * These are not actionable and create noise in Sentry
 *
 * See: https://github.com/gradle/website/issues/2262
 *
 * Example errors filtered:
 * - "Object Not Found Matching Id:6, MethodName:update, ParamCount:4"
 * - Similar RPC-style communication errors from browser extensions
 * - Non-Error promise rejections with these patterns
 *
 * @param event - The Sentry event being processed
 * @param hint - Additional information about the original exception
 * @returns The event to send to Sentry, or null to filter it out
 */
export function filterBrowserExtensionErrors(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // Get the error message from the event
  const error = hint.originalException;
  const errorMessage = error?.toString() || '';

  // Also check exception values in the event
  const exceptionValues = event.exception?.values || [];
  const hasExtensionError = exceptionValues.some(exception => {
    const value = exception.value || '';
    return (
      // Match the specific browser extension error pattern
      value.includes('Object Not Found Matching Id') ||
      (value.includes('MethodName:') && value.includes('ParamCount:')) ||
      // Also check for the error as a string
      value.includes('Non-Error promise rejection captured')
    );
  });

  // Check if the error message matches known browser extension patterns
  if (
    errorMessage.includes('Object Not Found Matching Id') ||
    (errorMessage.includes('MethodName:') && errorMessage.includes('ParamCount:')) ||
    hasExtensionError
  ) {
    // Don't send this error to Sentry
    return null;
  }

  // Send all other errors to Sentry
  return event;
}
